import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { type MapPort, WeatherMapGL } from '../components/WeatherMapGL'
import { Pill, Stat } from '../components/apple-ui'
import { useReveal } from '../components/marketing/motion'
import { KEYED_BASE } from '../lib/publicApi'
import { usePageMeta } from '../lib/usePageMeta'
import { type Cyclone, fetchCyclones } from '../lib/weather-api'

interface PortIndexRow {
  code: string
  name: string
  cc: string
  region: string
  lat: number
  lng: number
  laneCount: number
  sailings: number
}

const LEVEL_COLOR: Record<string, string> = {
  severe: 'var(--danger)',
  moderate: 'var(--warn)',
  mild: 'var(--signal)',
}

// Region display order: busiest basins first, others fall to the end alphabetically.
const REGION_ORDER = [
  'East Asia',
  'Southeast Asia',
  'South Asia',
  'Middle East',
  'Europe',
  'North America',
  'Latin America',
  'Africa',
  'Oceania',
  'Other',
]

export function PortsHubPage() {
  usePageMeta(
    'Port congestion tracker: live delays by port · SchedulesMCP',
    "Live congestion and delay status for the world's major container ports. See which ports are congested or storm-exposed right now, and the shipping lanes at risk. Free, updated hourly."
  )
  useReveal('main')
  const [ports, setPorts] = useState<PortIndexRow[]>([])
  const [levels, setLevels] = useState<Record<string, string>>({})
  const [cyclones, setCyclones] = useState<Cyclone[]>([])
  const [congestedOnly, setCongestedOnly] = useState(false)

  useEffect(() => {
    let live = true
    fetch('/data/ports/index.json')
      .then((r) => (r.ok ? r.json() : { ports: [] }))
      .then((d: { ports?: PortIndexRow[] }) => live && setPorts(d.ports ?? []))
      .catch(() => {})
    fetchCyclones()
      .then((c) => live && setCyclones(c))
      .catch(() => {})
    // Keyed since 07.10: through the site's key (site-api.js), so the badges stay on for every visitor.
    fetch(`${KEYED_BASE}/port-congestion`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { data?: { ports?: Array<{ port_unlocode: string; level: string }> } } | null) => {
        if (!live || !d?.data?.ports) return
        const m: Record<string, string> = {}
        for (const p of d.data.ports) m[p.port_unlocode] = (p.level ?? '').toLowerCase()
        setLevels(m)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  const mapPorts: MapPort[] = useMemo(
    () =>
      ports.map((p) => {
        const l = levels[p.code]
        return {
          code: p.code,
          name: p.name,
          lat: p.lat,
          lon: p.lng,
          level: l === 'severe' || l === 'moderate' || l === 'mild' ? l : null,
        }
      }),
    [ports, levels]
  )

  const stats = useMemo(() => {
    // Count congestion only for ports that are actually in the VISIBLE list; /data/ports/index.json is
    // pruned to ports with ≥3 lanes, while /port-congestion returns rows for ports absent from it.
    // Counting every congestion row inflated the "Congested"/"Severe" cards vs the filtered list.
    const lvls = ports.map((p) => levels[p.code]).filter(Boolean)
    return {
      total: ports.length,
      congested: lvls.filter((l) => l === 'severe' || l === 'moderate' || l === 'mild').length,
      severe: lvls.filter((l) => l === 'severe').length,
      regions: new Set(ports.map((p) => p.region)).size,
    }
  }, [ports, levels])

  const groups = useMemo(() => {
    const g = new Map<string, PortIndexRow[]>()
    for (const p of ports) {
      if (congestedOnly && !levels[p.code]) continue
      const arr = g.get(p.region) ?? []
      arr.push(p)
      g.set(p.region, arr)
    }
    for (const arr of g.values()) arr.sort((a, b) => b.sailings - a.sailings)
    return [...g.entries()].sort(
      (a, b) =>
        (REGION_ORDER.indexOf(a[0]) + 1 || 99) - (REGION_ORDER.indexOf(b[0]) + 1 || 99) ||
        a[0].localeCompare(b[0])
    )
  }, [ports, levels, congestedOnly])

  return (
    <main className="max-w-6xl mx-auto px-6 pt-12 pb-16">
      <nav className="text-sm text-[var(--text-muted)] mb-10">
        <Link to="/" className="no-underline hover:underline">
          SchedulesMCP
        </Link>{' '}
        › Port congestion
      </nav>

      {/* Hero: book §07 anatomy. Teal eyebrow, ultralight title, one-line hook, live lead. */}
      <header className="text-center mb-14">
        <p
          className="m-0 font-mono text-xs uppercase text-accent"
          style={{ letterSpacing: '0.14em' }}
        >
          Port congestion
        </p>
        <h1
          className="mx-auto mt-10 mb-0 font-display text-[clamp(28px,4.6vw,48px)] leading-[1.08] text-[var(--text-primary)]"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Live port congestion, worldwide
        </h1>
        <p className="mx-auto mt-10 mb-0 text-base leading-[1.5] text-[var(--text-secondary)]">
          Live congestion and delay status for the world's major container ports.
        </p>
        <p className="mx-auto mt-6 mb-0 max-w-[62ch] text-sm leading-relaxed text-[var(--text-muted)] text-justify [hyphens:auto]">
          Fused from port-call throughput, AIS dwell and the weather feeds. Open a port for its
          expected added wait, active-storm exposure and the shipping lanes at risk.
        </p>
      </header>

      {ports.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label="Ports tracked" value={stats.total} />
          <Stat label="Congested" value={stats.congested} accent={stats.congested > 0} />
          <Stat label="Severe" value={stats.severe} />
          <Stat label="Regions" value={stats.regions} />
        </div>
      )}

      {mapPorts.length > 0 && (
        <div className="mb-8">
          <WeatherMapGL cyclones={cyclones} ports={mapPorts} />
        </div>
      )}

      {ports.length === 0 ? (
        <div className="text-[var(--text-muted)]">Loading ports…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <Pill on={!congestedOnly} onClick={() => setCongestedOnly(false)}>
              All ports · {stats.total}
            </Pill>
            <Pill on={congestedOnly} onClick={() => setCongestedOnly(true)}>
              Congested only · {stats.congested}
            </Pill>
          </div>

          {groups.length === 0 ? (
            <p className="text-[var(--text-muted)] py-4">No congested ports right now.</p>
          ) : (
            groups.map(([region, rows]) => (
              <section key={region} className="mb-9">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2">
                  {region} · {rows.length}
                </h2>
                <ul className="list-none p-0 m-0 rounded-xl border border-border-strong overflow-hidden">
                  {rows.map((p, i) => {
                    const lvl = levels[p.code]
                    const color = lvl ? LEVEL_COLOR[lvl] : null
                    return (
                      <li key={p.code}>
                        <Link
                          to={`/ports/${p.code}`}
                          className={`flex items-center gap-3 px-4 py-2.5 no-underline hover:bg-[var(--surface-1)] transition-colors ${i > 0 ? 'border-t border-border' : ''}`}
                        >
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: color ?? 'var(--text-faint)' }}
                            title={color ? `${lvl} congestion` : 'no congestion flagged'}
                          />
                          <span className="text-[var(--text-primary)] font-medium">{p.name}</span>
                          <span className="font-mono text-xs text-[var(--text-muted)]">
                            {p.code}
                          </span>
                          <span className="text-xs text-[var(--text-muted)] ml-auto shrink-0">
                            {p.laneCount} lanes
                          </span>
                          {color && (
                            <span
                              className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0"
                              style={{ color, background: 'rgb(var(--surface-2-rgb))' }}
                            >
                              {lvl}
                            </span>
                          )}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </section>
            ))
          )}
        </>
      )}

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/disruptions"
          className="bg-signal text-on-accent font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
        >
          Live disruption map
        </Link>
        <Link
          to="/compare"
          className="border border-border px-6 py-3 rounded-lg no-underline hover:border-signal"
        >
          Compare carriers
        </Link>
      </div>
    </main>
  )
}
